/**
 * Browser Client (Singleton)
 * Based on auth-system.md specification
 *
 * The browser client is a singleton - only one instance exists per browser session.
 * This prevents multiple connections and ensures consistent state.
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

import {
  readViewAsRecord,
  VIEW_AS_AUTH_STORAGE_KEY,
} from "./viewAs";
import { createViewAsFetch } from "./viewAsFetch";

// Singleton instance
let browserClient: SupabaseClient | null = null;

interface EnvironmentConfig {
  url: string;
  publishableKey: string;
}

function getEnvironment(): EnvironmentConfig | null {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const publishableKey =
    import.meta.env.VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY ||
    import.meta.env.VITE_SUPABASE_ANON_KEY;

  const isValidUrl =
    url && (url.startsWith("http://") || url.startsWith("https://"));

  if (!isValidUrl || !publishableKey) {
    return null;
  }

  return { url, publishableKey };
}

// Use a no-op lock to prevent AbortError issues with navigator.locks
// This is safe because we're using a singleton client pattern
// and the RLS policies handle concurrent access properly
const noopLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>,
): Promise<R> => {
  // Simply execute the function without locking
  // The singleton pattern ensures only one client exists
  return await fn();
};

/**
 * Creates a client bound to an impersonated ("view as user") session.
 *
 * Three differences from the ordinary client, each load-bearing:
 *
 *  - **sessionStorage, not localStorage.** The impersonated session must not
 *    outlive the tab and must not be visible to the admin's other tabs.
 *  - **A distinct storage key.** Sharing `moe-auth-token` would overwrite the
 *    Super Admin's own session with the target's, signing them out of their own
 *    account everywhere.
 *  - **`detectSessionInUrl: false`.** This tab is opened by script and may
 *    carry fragments from the admin's own navigation; it must never adopt one.
 *
 * `global.fetch` is wrapped so writes are refused unless the Super Admin has
 * explicitly enabled actions. The flag is read through a getter rather than
 * captured, because it can be flipped in the middle of a session.
 */
export function createViewAsClient(): SupabaseClient | null {
  const env = getEnvironment();
  if (!env) return null;

  return createClient(env.url, env.publishableKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
      flowType: "pkce",
      storageKey: VIEW_AS_AUTH_STORAGE_KEY,
      storage: typeof window !== "undefined"
        ? window.sessionStorage
        : undefined,
      lock: noopLock,
      debug: import.meta.env.DEV,
    },
    global: {
      fetch: createViewAsFetch(
        () => readViewAsRecord()?.actionsEnabled === true,
      ),
    },
  });
}

/**
 * Creates or returns the singleton Supabase browser client
 *
 * In a tab that holds a view-as record, this returns the impersonated client
 * instead. Because every service reaches Supabase through the proxy in
 * `src/services/supabase.ts`, which resolves this singleton on each property
 * access, that one branch switches the entire application to the target's
 * identity without touching a single call site.
 *
 * @returns SupabaseClient or null if not configured
 */
export function createBrowserClient(): SupabaseClient | null {
  // Return existing client if already created
  if (browserClient) {
    return browserClient;
  }

  const env = getEnvironment();

  if (!env) {
    if (typeof window !== "undefined") {
      console.warn(
        "[Supabase Client] Not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_DEFAULT_KEY (or VITE_SUPABASE_ANON_KEY) environment variables.",
      );
    }
    return null;
  }

  if (readViewAsRecord()) {
    browserClient = createViewAsClient();
    return browserClient;
  }

  browserClient = createClient(env.url, env.publishableKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      flowType: "pkce",
      storageKey: "moe-auth-token",
      storage: typeof window !== "undefined" ? window.localStorage : undefined,
      lock: noopLock,
      // Enable debug mode to diagnose auth issues
      // Set to false in production
      debug: import.meta.env.DEV,
    },
  });

  return browserClient;
}

/**
 * Get existing client without creating (for checking state)
 */
export function getBrowserClient(): SupabaseClient | null {
  return browserClient;
}

/**
 * Helper to check if Supabase is properly configured
 */
export function isSupabaseConfigured(): boolean {
  return getEnvironment() !== null;
}

/**
 * Reset the browser client (useful for testing or logout)
 */
export function resetBrowserClient(): void {
  browserClient = null;
}
